/**
 * AgentGlassBoxDetail — adapts an AgentTask's glass-box fields into the
 * canonical GlassBoxTrace primitive plus agent-specific extras
 * (reasoning trace steps and version stamp).
 *
 * The four canonical sections (confidence, cited evidence, alternatives,
 * counterfactuals) are rendered by deriving a GlassBoxTrace from the task
 * and passing it to <GlassBoxTrace>. Agent-specific fields (trace steps,
 * version stamp, citation excerpts) are rendered below as an extension.
 */

import type { AgentTask, AgentCitation } from '@septima/contract';
import { useState } from 'react';
import { ChevronDown, ChevronRight, Compass, Fingerprint } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { GlassBoxTrace as GlassBoxTraceType, TypedCitation, CitationKind } from '@/data/types';
import GlassBoxTrace from './GlassBoxTrace';

interface AgentGlassBoxDetailProps {
    task: AgentTask;
    defaultOpen?: boolean;
}

/**
 * Maps AgentCitationSource to the canonical CitationKind superset.
 * Sources not in CitationKind fall back to 'audit_log'.
 */
function sourceToKind(source: AgentCitation['source']): CitationKind {
    const MAP: Partial<Record<AgentCitation['source'], CitationKind>> = {
        fca_handbook: 'fca_handbook',
        firm_policy: 'firm_policy',
        fund_policy: 'fund_policy',
        kb_note: 'kb_note',
        transcript: 'transcript',
        market_data: 'market_data',
        audit_log: 'audit_log',
        custodian_feed: 'audit_log',
        breach_register: 'audit_log',
        mandate: 'firm_policy',
        fact_find: 'kb_note',
    };
    return MAP[source] ?? 'audit_log';
}

function agentTaskToTrace(task: AgentTask): GlassBoxTraceType | null {
    const hasContent =
        task.confidence ||
        (task.citations && task.citations.length > 0) ||
        (task.alternativesConsidered && task.alternativesConsidered.length > 0) ||
        task.counterfactualAnchor;

    if (!hasContent) return null;

    const citedEvidence: TypedCitation[] = (task.citations ?? []).map((c) => ({
        kind: sourceToKind(c.source),
        ref: c.id,
        label: c.label,
        href: c.href,
    }));

    const alternatives = (task.alternativesConsidered ?? []).map((a) => ({
        option: a.conclusion,
        whyNotChosen: a.rejectedBecause,
    }));

    // AgentTrace stores the anchor as one sentence with no separate
    // conditions, so mapping conditions[] alone dropped the anchor entirely
    // (SEP-22). Fall back to a single summary-only entry.
    const anchor = task.counterfactualAnchor;
    const counterfactuals = !anchor
        ? []
        : anchor.conditions.length > 0
          ? anchor.conditions.map((cond) => ({
                condition: cond,
                wouldChangeTo: anchor.summary,
            }))
          : [{ condition: 'Anchor condition', wouldChangeTo: anchor.summary }];

    return {
        confidenceRationale: task.confidence?.rationale ?? '',
        confidenceScore: task.confidence?.score,
        citedEvidence,
        alternatives,
        counterfactuals,
    };
}

export default function AgentGlassBoxDetail({
    task,
    defaultOpen = false,
}: AgentGlassBoxDetailProps) {
    const trace = agentTaskToTrace(task);
    const hasExtras =
        (task.trace && task.trace.length > 0) ||
        task.versionStamp ||
        task.citations?.some((c) => c.excerpt);

    // If neither canonical trace nor extras exist, render nothing.
    if (!trace && !hasExtras) return null;

    // If no canonical trace but extras exist, render extras-only in a wrapper.
    if (!trace) {
        return (
            <ExtrasOnlySection
                task={task}
                hasTrace={!!(task.trace && task.trace.length > 0)}
                defaultOpen={defaultOpen}
            />
        );
    }

    return (
        <div className="flex flex-col gap-1.5">
            {/* Canonical GlassBoxTrace primitive */}
            <GlassBoxTrace trace={trace} defaultOpen={defaultOpen} />

            {/* Agent-specific extras: reasoning trace steps + version stamp */}
            {hasExtras && <AgentExtras task={task} />}
        </div>
    );
}

// ─── Extras: reasoning trace steps + version stamp ───────────────────────────

function SectionHeader({ icon: Icon, label }: { icon: typeof Compass; label: string }) {
    return (
        <div className="flex items-center gap-1.5 text-card-title text-[rgb(var(--color-text-primary))]">
            <Icon className="w-3 h-3" strokeWidth={2} />
            {label}
        </div>
    );
}

function AgentExtras({ task }: { task: AgentTask }) {
    const [open, setOpen] = useState(false);

    const hasTrace = !!(task.trace && task.trace.length > 0);
    const hasStamp = !!task.versionStamp;
    const hasExcerpts = task.citations?.some((c) => c.excerpt);

    if (!hasTrace && !hasStamp && !hasExcerpts) return null;

    return (
        <section
            className={cn(
                'rounded-md border border-[rgb(var(--color-divider))]',
                'bg-[rgb(var(--color-surface-secondary)/0.6)]',
            )}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left',
                    'text-card-title text-[rgb(var(--color-text-secondary))]',
                    'hover:text-[rgb(var(--color-text-primary))] transition-colors',
                )}
            >
                {open ? (
                    <ChevronDown className="w-3 h-3" strokeWidth={2} />
                ) : (
                    <ChevronRight className="w-3 h-3" strokeWidth={2} />
                )}
                <span className="flex-1">Reasoning trace &amp; version</span>
            </button>

            {open && (
                <div className="flex flex-col gap-3 px-3 pb-3 border-t border-[rgb(var(--color-divider)/0.6)] pt-3">
                    {hasExcerpts && (
                        <ExcerptsBlock citations={task.citations!.filter((c) => c.excerpt)} />
                    )}
                    {hasTrace && <TraceBlock trace={task.trace!} />}
                    {hasStamp && <VersionStampBlock stamp={task.versionStamp!} />}
                </div>
            )}
        </section>
    );
}

/** Extras-only wrapper for tasks that have trace/stamp but no canonical trace. */
function ExtrasOnlySection({
    task,
    hasTrace,
    defaultOpen,
}: {
    task: AgentTask;
    hasTrace: boolean;
    defaultOpen: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    if (!hasTrace && !task.versionStamp) return null;
    return (
        <section
            className={cn(
                'rounded-md border border-[rgb(var(--color-divider))]',
                'bg-[rgb(var(--color-surface-secondary)/0.6)]',
            )}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left',
                    'text-card-title text-[rgb(var(--color-text-secondary))]',
                    'hover:text-[rgb(var(--color-text-primary))] transition-colors',
                )}
            >
                {open ? (
                    <ChevronDown className="w-3 h-3" strokeWidth={2} />
                ) : (
                    <ChevronRight className="w-3 h-3" strokeWidth={2} />
                )}
                <span className="flex-1">Glass-box trace</span>
            </button>
            {open && (
                <div className="flex flex-col gap-3 px-3 pb-3 border-t border-[rgb(var(--color-divider)/0.6)] pt-3">
                    {hasTrace && <TraceBlock trace={task.trace!} />}
                    {task.versionStamp && <VersionStampBlock stamp={task.versionStamp} />}
                </div>
            )}
        </section>
    );
}

function ExcerptsBlock({ citations }: { citations: AgentCitation[] }) {
    return (
        <div className="flex flex-col gap-1">
            <SectionHeader icon={Compass} label="Source excerpts" />
            <ul className="flex flex-col gap-1">
                {citations.map((c) => (
                    <li key={c.id}>
                        <span className="text-annotation font-medium text-[rgb(var(--color-text-tertiary))] mr-1">
                            {c.label}:
                        </span>
                        <span className="text-annotation text-[rgb(var(--color-text-secondary))] italic">
                            &ldquo;{c.excerpt}&rdquo;
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function TraceBlock({ trace }: { trace: NonNullable<AgentTask['trace']> }) {
    return (
        <div className="flex flex-col gap-1.5">
            <SectionHeader icon={Compass} label="Reasoning trace" />
            <ol className="flex flex-col gap-1.5 border-l border-[rgb(var(--color-divider))] pl-3">
                {trace.map((step, i) => (
                    <li key={i} className="flex flex-col gap-0.5">
                        <div className="text-annotation font-medium text-[rgb(var(--color-text-primary))]">
                            {step.label}
                        </div>
                        {step.reasoning && (
                            <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug">
                                {step.reasoning}
                            </p>
                        )}
                        {(step.input || step.output) && (
                            <div className="grid grid-cols-2 gap-2 text-annotation text-[rgb(var(--color-text-tertiary))]">
                                {step.input && (
                                    <div>
                                        <span className="font-medium">in:</span> {step.input}
                                    </div>
                                )}
                                {step.output && (
                                    <div>
                                        <span className="font-medium">out:</span> {step.output}
                                    </div>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function VersionStampBlock({ stamp }: { stamp: NonNullable<AgentTask['versionStamp']> }) {
    return (
        <div className="flex flex-col gap-1 pt-2 border-t border-[rgb(var(--color-divider)/0.6)]">
            <SectionHeader icon={Fingerprint} label="Version stamp" />
            <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-annotation font-mono">
                <dt className="text-[rgb(var(--color-text-tertiary))]">model</dt>
                <dd className="text-[rgb(var(--color-text-secondary))]">{stamp.modelId}</dd>
                <dt className="text-[rgb(var(--color-text-tertiary))]">prompt</dt>
                <dd className="text-[rgb(var(--color-text-secondary))]">{stamp.promptVersion}</dd>
                <dt className="text-[rgb(var(--color-text-tertiary))]">ruleset</dt>
                <dd className="text-[rgb(var(--color-text-secondary))]">{stamp.rulesetVersion}</dd>
                <dt className="text-[rgb(var(--color-text-tertiary))]">snapshot</dt>
                <dd className="text-[rgb(var(--color-text-secondary))]">{stamp.dataSnapshotAt}</dd>
            </dl>
        </div>
    );
}
